import { useState, type FormEvent } from "react";
import { useMutation } from "@tanstack/react-query";
import { Pencil, Trash2, Truck } from "lucide-react";

import { inventoryService, type PurchaseOrder, type Supplier } from "../../services/inventoryService";
import { apiErrorMessage } from "../../utils/apiError";
import { todayIso } from "../../utils/dates";
import { Field, FormSection } from "../FormLayout";
import { FormModal } from "../FormModal";
import { ClientRegister, StatusDot, type RegisterColumn } from "../register/ClientRegister";
import { DetailsModal } from "../register/DetailsModal";
import { Notice, type NoticeState } from "../register/RecordParts";
import { errorLine } from "./InventoryParts";
import { RATINGS, day, labelFrom, money, orderStatus, supplierConfig } from "./inventoryLogic";

function SupplierForm({ supplier, onClose, onDone }: { supplier?: Supplier; onClose: () => void; onDone: (supplier: Supplier) => void }) {
  const save = useMutation({
    mutationFn: (data: Partial<Supplier>) => (supplier ? inventoryService.updateSupplier(supplier.id, data) : inventoryService.createSupplier(data)),
    onSuccess: onDone,
  });
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const text = (name: string) => String(form.get(name) ?? "").trim() || null;
    save.mutate({
      name: text("name") ?? "",
      contact_person: text("contact_person"),
      phone: text("phone"),
      email: text("email"),
      address: text("address"),
      website: text("website"),
      payment_terms: text("payment_terms"),
      credit_limit: text("credit_limit"),
      rating: Number(text("rating") ?? 3),
      notes: text("notes"),
      ...(supplier ? {} : { is_active: true }),
    });
  };
  const value = (name: keyof Supplier) => (supplier?.[name] as string | number | null | undefined) ?? "";
  return (
    <FormModal
      id="supplier"
      title={supplier ? `Edit ${supplier.name}` : "Add a supplier"}
      busy={save.isPending}
      error={save.isError ? apiErrorMessage(save.error, "The supplier could not be saved.") : undefined}
      saveLabel={supplier ? "Save changes" : "Add the supplier"}
      onClose={onClose}
      onSubmit={submit}
    >
      <FormSection title="The supplier" divided={false}>
        <Field label="Name" className="sm:col-span-2">
          <input required name="name" maxLength={200} defaultValue={value("name")} className="input mt-1" placeholder="e.g. Uganda Stationers Ltd" />
        </Field>
        <Field label="Who to speak to" optional>
          <input name="contact_person" maxLength={100} defaultValue={value("contact_person")} className="input mt-1" />
        </Field>
        <Field label="Phone" optional>
          <input type="tel" name="phone" maxLength={20} defaultValue={value("phone")} className="input mt-1" placeholder="e.g. 0772 123456" />
        </Field>
        <Field label="Email" optional>
          <input type="email" name="email" defaultValue={value("email")} className="input mt-1" />
        </Field>
        <Field label="Website" optional>
          <input type="url" name="website" defaultValue={value("website")} className="input mt-1" placeholder="https://" />
        </Field>
        <Field label="Address" optional className="sm:col-span-2">
          <textarea name="address" rows={2} defaultValue={value("address")} className="input mt-1" />
        </Field>
      </FormSection>
      <FormSection title="Terms" columns={3}>
        <Field label="Payment terms" optional hint="e.g. 30 days, cash on delivery.">
          <input name="payment_terms" maxLength={100} defaultValue={value("payment_terms")} className="input mt-1" />
        </Field>
        <Field label="Credit limit (UGX)" optional>
          <input type="number" name="credit_limit" min={0} step="any" defaultValue={value("credit_limit")} className="input mt-1 tabular-nums" />
        </Field>
        <Field label="How good are they">
          <select name="rating" defaultValue={String(value("rating") || 3)} className="input mt-1">
            {RATINGS.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
          </select>
        </Field>
        <Field label="Notes" optional className="sm:col-span-2 lg:col-span-3">
          <textarea name="notes" rows={2} defaultValue={value("notes")} className="input mt-1" />
        </Field>
      </FormSection>
    </FormModal>
  );
}

/** Who the school buys from, and what it has ordered from each. */
export function SuppliersPanel({ suppliers, orders, isLoading, isError, onRetry, onChanged }: {
  suppliers?: Supplier[];
  orders: PurchaseOrder[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  onChanged: () => void;
}) {
  const today = todayIso();
  const [notice, setNotice] = useState<NoticeState>(null);
  const [viewing, setViewing] = useState<number | null>(null);
  const [editing, setEditing] = useState<Supplier | "new" | null>(null);
  const toggle = useMutation({
    mutationFn: (row: Supplier) => inventoryService.updateSupplier(row.id, { is_active: !row.is_active }),
    onSuccess: (row) => {
      setNotice({ tone: "success", text: row.is_active ? `${row.name} is active again.` : `${row.name} is inactive; no new orders can go to them.` });
      onChanged();
    },
  });
  const remove = useMutation({
    mutationFn: inventoryService.deleteSupplier,
    onSuccess: () => {
      setViewing(null);
      setNotice({ tone: "success", text: "The supplier was deleted." });
      onChanged();
    },
  });
  const ordersBy = (id: number) => orders.filter((row) => row.supplier === id);
  const spent = (id: number) => ordersBy(id).filter((row) => row.status === "received").reduce((sum, row) => sum + Number(row.total_amount), 0);

  const columns: RegisterColumn<Supplier>[] = [
    { header: "Supplier", cell: (row) => <span className="font-medium text-gray-900">{row.name}</span>, exportValue: (row) => row.name, className: "min-w-[12rem]" },
    { header: "Contact", cell: (row) => row.contact_person || "—", exportValue: (row) => row.contact_person ?? "", className: "text-gray-700" },
    { header: "Phone", cell: (row) => row.phone || "—", exportValue: (row) => row.phone ?? "", className: "text-gray-700" },
    { header: "Email", cell: (row) => row.email || "—", exportValue: (row) => row.email ?? "", className: "text-gray-700" },
    { header: "Terms", cell: (row) => row.payment_terms || "—", exportValue: (row) => row.payment_terms ?? "", className: "text-gray-700" },
    { header: "Orders", cell: (row) => ordersBy(row.id).length, exportValue: (row) => ordersBy(row.id).length, align: "right" },
    { header: "Received value", cell: (row) => money(spent(row.id)), exportValue: (row) => spent(row.id), align: "right" },
    { header: "Status", cell: (row) => <StatusDot label={row.is_active ? "Active" : "Inactive"} dot={row.is_active ? "bg-emerald-500" : "bg-gray-400"} />, exportValue: (row) => (row.is_active ? "Active" : "Inactive") },
  ];

  const supplier = viewing !== null ? suppliers?.find((row) => row.id === viewing) : undefined;
  const history = supplier ? ordersBy(supplier.id) : [];

  return (
    <div className="space-y-4">
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <ClientRegister
        title="Suppliers"
        description="Open a supplier for their details and orders."
        actions={
          <button type="button" onClick={() => setEditing("new")} className="btn-primary h-8 min-h-8 px-3 text-xs">
            <Truck className="mr-1.5 h-3.5 w-3.5" /> Add a supplier
          </button>
        }
        rows={suppliers}
        config={supplierConfig}
        columns={columns}
        rowKey={(row) => row.id}
        onOpen={(row) => {
          toggle.reset();
          remove.reset();
          setViewing(row.id);
        }}
        noun={["supplier", "suppliers"]}
        exportName={{ file: "suppliers", title: "Suppliers" }}
        isLoading={isLoading}
        isError={isError}
        onRetry={onRetry}
        emptyText="No suppliers yet. Add who the school buys from."
      />

      {supplier && !editing && (
        <DetailsModal
          title={supplier.name}
          headline={{ label: "Received from them", value: money(spent(supplier.id)) || "UGX 0", note: `${history.length} ${history.length === 1 ? "order" : "orders"}` }}
          status={{ label: supplier.is_active ? "Active" : "Inactive", dot: supplier.is_active ? "bg-emerald-500" : "bg-gray-400" }}
          facts={[
            ["Who to speak to", supplier.contact_person ?? undefined],
            ["Phone", supplier.phone ? <a key="phone" href={`tel:${supplier.phone}`} className="text-primary-700 hover:underline">{supplier.phone}</a> : undefined],
            ["Email", supplier.email ? <a key="email" href={`mailto:${supplier.email}`} className="text-primary-700 hover:underline">{supplier.email}</a> : undefined],
            ["Website", supplier.website ?? undefined],
            ["Payment terms", supplier.payment_terms ?? undefined],
            ["Credit limit", money(supplier.credit_limit) || undefined],
            ["How good", labelFrom(RATINGS, String(supplier.rating ?? 3))],
          ]}
          sections={[
            { title: "Address", body: supplier.address },
            { title: "Notes", body: supplier.notes },
          ]}
          onClose={() => setViewing(null)}
          footer={
            <>
              {!history.length && (
                <button type="button" disabled={remove.isPending} onClick={() => remove.mutate(supplier.id)} className="mr-auto inline-flex items-center gap-1.5 rounded-md border border-red-200 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50">
                  <Trash2 className="h-4 w-4" /> Delete
                </button>
              )}
              <button type="button" className="btn btn-outline" onClick={() => setViewing(null)}>Close</button>
              <button type="button" disabled={toggle.isPending} onClick={() => toggle.mutate(supplier)} className="btn btn-outline">
                {supplier.is_active ? "Make inactive" : "Make active"}
              </button>
              <button type="button" className="btn btn-primary gap-1.5" onClick={() => setEditing(supplier)}>
                <Pencil className="h-4 w-4" /> Edit
              </button>
            </>
          }
        >
          {history.length > 0 && (
            <p className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">It has orders, so it can't be deleted; make it inactive instead.</p>
          )}
          {toggle.isError && errorLine(toggle.error, "The supplier could not be changed.")}
          {remove.isError && errorLine(remove.error, "The supplier could not be deleted.")}
          <section>
            <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Orders</h4>
            {history.length ? (
              <ul className="mt-1.5 divide-y divide-gray-100 rounded-lg border border-gray-200">
                {history.slice(0, 8).map((row) => (
                  <li key={row.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm">
                    <span>
                      <span className="font-medium text-gray-900">{row.order_number}</span>
                      <span className="ml-2 tabular-nums text-gray-500">{day(row.order_date)}</span>
                    </span>
                    <span className="flex items-center gap-3">
                      <span className="tabular-nums text-gray-700">{money(row.total_amount)}</span>
                      <StatusDot {...orderStatus(row, today)} />
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-1.5 text-sm text-gray-500">No orders yet.</p>
            )}
          </section>
        </DetailsModal>
      )}

      {editing && (
        <SupplierForm
          supplier={editing === "new" ? undefined : editing}
          onClose={() => setEditing(null)}
          onDone={(saved) => {
            const created = editing === "new";
            setEditing(null);
            setNotice({ tone: "success", text: created ? `${saved.name} added.` : `${saved.name} updated.` });
            setViewing(saved.id);
            onChanged();
          }}
        />
      )}
    </div>
  );
}
